import { DEFAULT_THEME, type ThemePrefs } from "@qn/ui";
import { create } from "zustand";
import type { ModelRef } from "./api";

export type Locale = "en" | "ar";

interface PrefsState extends ThemePrefs {
  locale: Locale;
  /** Last model the user picked for a new chat (device-local convenience). */
  lastModel: ModelRef | null;
  /** Auto: pick the model per message (fast for quick chat, strongest for hard ones, tool-capable for agent work). */
  autoModel: boolean;
  /** What Auto chose for the last message (shown in the picker). */
  lastAuto: { label: string; provider: string; reason: string } | null;
  sidebarOpen: boolean;
  /** Enter sends (Shift+Enter = new line). When off, Ctrl/Cmd+Enter sends. */
  sendOnEnter: boolean;
  chatWidth: "narrow" | "normal" | "wide";
  readingFont: "sans" | "serif" | "mono";
  reduceMotion: boolean;
  /** Slowly drifting aurora behind the app (off by default; never runs with reduced motion). */
  ambient: boolean;
  lockZoom: boolean;
  /** What a message becomes: auto = detect (image / document / chat), text = never generate images, image = always generate an image. */
  sendMode: "auto" | "text" | "image";
  /** Reload automatically when a newer version is deployed and you come back to the app. */
  autoUpdate: boolean;
  showUsage: boolean;
  /** Text in, voice out: read every finished reply aloud (language and dialect matched). */
  readAloud: boolean;
  /** Inside voice mode: speak the replies, or only show them as text (voice in, text out). */
  voiceReplies: "voice" | "text";
  /** Agent mode (tools + autonomous runs) for new messages. */
  agentMode: boolean;
  /** Agent persona for new chats (null = orchestrator). */
  lastAgentId: string | null;
  set: (patch: Partial<Omit<PrefsState, "set">>) => void;
}

const KEY = "qn-prefs";

function load(): Partial<PrefsState> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? "{}");
  } catch {
    return {};
  }
}

export const usePrefs = create<PrefsState>((set, get) => ({
  ...DEFAULT_THEME,
  locale: (typeof navigator !== "undefined" && navigator.language?.startsWith("ar")
    ? "ar"
    : "en") as Locale,
  lastModel: null,
  autoModel: true,
  lastAuto: null,
  agentMode: false,
  readAloud: false,
  voiceReplies: "voice" as const,
  sendOnEnter: true,
  chatWidth: "normal" as const,
  readingFont: "sans" as const,
  reduceMotion: false,
  ambient: false,
  lockZoom: true,
  sendMode: "auto" as const,
  autoUpdate: false,
  showUsage: true,
  lastAgentId: null,
  sidebarOpen: typeof window === "undefined" ? true : window.innerWidth >= 1024,
  ...load(),
  set: (patch) => {
    set(patch);
    const { set: _s, sidebarOpen: _o, ...persist } = get();
    try {
      localStorage.setItem(KEY, JSON.stringify(persist));
    } catch {
      /* private mode */
    }
  },
}));
